import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
const DesignSystemPage = import.meta.env.DEV ? React.lazy(() => import('./pages/DesignSystemPage')) : null;
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const PublicCasesPage = React.lazy(() => import('./pages/PublicCasesPage'));
import { DosarProvider } from './context/DosarContext';
import DosarDrawer from './components/DosarDrawer';
import DosarToast from './components/DosarToast';
import { ManualProvider } from './components/manual/ManualContext';
import ManualDrawer from './components/manual/ManualDrawer';
import ManualButton from './components/manual/ManualButton';
import { AuthProvider } from './context/AuthContext';
import SearchSessionBoundary from './components/SearchSessionBoundary';
import { useAuth } from './context/AuthContext.shared';
import { LanguageProvider } from './context/LanguageContext';
import { PublicSettingsProvider } from './context/PublicSettingsContext';
import { usePublicSettings } from './context/PublicSettingsContext.shared';
import ServiceUnavailable from './components/ServiceUnavailable';
import { Loader2 } from 'lucide-react';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';


import { initGA } from './services/analytics';
import AnalyticsTracker from './components/AnalyticsTracker';
const PremiumHomePage = React.lazy(() => import('./pages/PremiumHomePage'));
import NotFoundPage from './pages/NotFoundPage';
import CookiePolicyFloatingButton from './components/CookiePolicyFloatingButton';
import { useNonMainSEO } from './hooks/useNonMainSEO';

// Initialize Google Analytics
initGA();

// Lazy load heavy or less frequently used pages
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ActiveLearningPage = React.lazy(() => import('./pages/ActiveLearningPage'));
const MemorizationPage = React.lazy(() => import('./pages/MemorizationPage'));
const LegalSchemesPage = React.lazy(() => import('./pages/LegalSchemesPage'));
const ImportAnalogicPage = React.lazy(() => import('./pages/ImportAnalogicPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const HelpCenterPage = React.lazy(() => import('./pages/HelpCenterPage'));
const GDPRPage = React.lazy(() => import('./pages/GDPRPage'));
const ReturnPolicyPage = React.lazy(() => import('./pages/ReturnPolicyPage'));
const SurseDatePage = React.lazy(() => import('./pages/SurseDatePage'));
const DocumentEditorPage = React.lazy(() => import('./pages/DocumentEditorPage'));

const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const TestAnalysisPage = React.lazy(() => import('./pages/TestAnalysisPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const CompleteProfilePage = React.lazy(() => import('./pages/CompleteProfilePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const SecuritatePage = React.lazy(() => import('./pages/SecuritatePage'));
// Ensure this is definitely here

const GridTestsPage = React.lazy(() => import('./pages/GridTestsPage'));
const PaymentReturnPage = React.lazy(() => import('./pages/PaymentReturnPage'));
const AdminBillingPage = React.lazy(() => import('./pages/AdminBillingPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const PromoLandingPage = React.lazy(() => import('./pages/PromoLandingPage'));
const PromoRegulationPage = React.lazy(() => import('./pages/PromoRegulationPage'));
const PromoRegisterPage = React.lazy(() => import('./pages/PromoRegisterPage'));
const PromoVerifyEmailPage = React.lazy(() => import('./pages/PromoVerifyEmailPage'));
const InstagramPromoShortcutPage = React.lazy(() => import('./pages/InstagramPromoShortcutPage'));
const TaxaTimbruPage = React.lazy(() => import('./pages/TaxaTimbruPage'));
const CalculTermenePage = React.lazy(() => import('./pages/CalculTermenePage'));
const CalculatorPensieAlimentaraPage = React.lazy(() => import('./pages/CalculatorPensieAlimentaraPage'));
const CalculatorTermeneLegalePage = React.lazy(() => import('./pages/CalculatorTermeneLegalePage'));
const CalculatorSalariuNetPage = React.lazy(() => import('./pages/CalculatorSalariuNetPage'));
const CalculProcentePage = React.lazy(() => import('./pages/CalculProcentePage'));
const CalculatorContopiriPage = React.lazy(() => import('./pages/CalculatorContopiriPage'));
const CalculatorLimitePage = React.lazy(() => import('./pages/CalculatorLimitePage'));
const CalculatorPrescriptiePage = React.lazy(() => import('./pages/CalculatorPrescriptiePage'));
const CalculatorReabilitarePage = React.lazy(() => import('./pages/CalculatorReabilitarePage'));
const LawyerAssistancePage = React.lazy(() => import('./pages/LawyerAssistancePage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const Sitemap = React.lazy(() => import('./pages/Sitemap'));
const UsefulLinksPage = React.lazy(() => import('./pages/UsefulLinksPage'));
const ManualMetodologiePage = React.lazy(() => import('./pages/ManualMetodologiePage'));
const CedoPage = React.lazy(() => import('./pages/CedoPage'));
const CedoDetailPage = React.lazy(() => import('./pages/CedoDetailPage'));
const CedoFisePage = React.lazy(() => import('./pages/CedoFisePage'));
const CedoFiseDetailPage = React.lazy(() => import('./pages/CedoFiseDetailPage'));
const PracticaNeunitaraPage = React.lazy(() => import('./pages/PracticaNeunitaraPage'));
const PracticaNeunitaraDetailPage = React.lazy(() => import('./pages/PracticaNeunitaraDetailPage'));
const JurnalPracticaJudiciaraPage = React.lazy(() => import('./pages/JurnalPracticaJudiciaraPage'));
const JurnalPracticaJudiciaraDetailPage = React.lazy(() => import('./pages/JurnalPracticaJudiciaraDetailPage'));
const IccjPage = React.lazy(() => import('./pages/IccjPage'));
const IccjDetailPage = React.lazy(() => import('./pages/IccjDetailPage'));

const LegalNewsPage = React.lazy(() => import('./pages/LegalNews/LegalNewsPage'));
const EditorDashboard = React.lazy(() => import('./pages/EditorDashboard/EditorDashboard'));
const ArticleEditor = React.lazy(() => import('./pages/EditorDashboard/ArticleEditor'));
const EcrisIngestPage = React.lazy(() => import('./pages/EditorDashboard/EcrisIngestPage'));
const ArticleDetailPage = React.lazy(() => import('./pages/LegalNews/ArticleDetailPage'));
const ProfessionalProfilePage = React.lazy(() => import('./pages/LegalNews/ProfessionalProfilePage'));
const PublicareArticolePage = React.lazy(() => import('./pages/LegalNews/PublicareArticolePage'));
const LegislatiePage = React.lazy(() => import('./pages/LegislatiePage'));
const AudioLegislatiePage = React.lazy(() => import('./pages/AudioLegislatiePage'));
const LegislatieCategoryPage = React.lazy(() => import('./pages/LegislatieCategoryPage'));
const LegislationSelectionPage = React.lazy(() => import('./pages/LegislationSelectionPage'));
const LegislatieDetailPage = React.lazy(() => import('./pages/LegislatieDetailPage'));
const PredictiveReportPage = React.lazy(() => import('./pages/PredictiveReportPage'));
const FinancePage = React.lazy(() => import('./pages/FinancePage'));

// SEO Landing Pages
const AgendaJuridicaPage = React.lazy(() => import('./pages/seo_landings/AgendaJuridicaPage'));
const JuristulVirtualPage = React.lazy(() => import('./pages/seo_landings/JuristulVirtualPage'));
const AsistentJuridicPage = React.lazy(() => import('./pages/seo_landings/AsistentJuridicPage'));
const InteligentArtificialaPage = React.lazy(() => import('./pages/seo_landings/InteligentArtificialaPage'));
const AccesJustitiePage = React.lazy(() => import('./pages/seo_landings/AccesJustitiePage'));
const ColegulAIPage = React.lazy(() => import('./pages/seo_landings/ColegulAIPage'));
const StudentiPage = React.lazy(() => import('./pages/seo_landings/StudentiPage'));
const PerspectiveJuridicePage = React.lazy(() => import('./pages/PerspectiveJuridicePage'));
const PredictieJuridicaPage = React.lazy(() => import('./pages/seo_landings/PredictieJuridicaPage'));
const ResurseJuridicePage = React.lazy(() => import('./pages/seo_landings/ResurseJuridicePage'));
const CrmStocarePrivataPage = React.lazy(() => import('./pages/seo_landings/CrmStocarePrivataPage'));
const AiPentruJuristiPage = React.lazy(() => import('./pages/seo_landings/AiPentruJuristiPage'));
const AiJuridicRomaniaPage = React.lazy(() => import('./pages/seo_landings/AiJuridicRomaniaPage'));
const CelMaiBunAiJuridicPage = React.lazy(() => import('./pages/seo_landings/CelMaiBunAiJuridicPage'));
const AsistentJuridicAiPage = React.lazy(() => import('./pages/seo_landings/AsistentJuridicAiPage'));
const JuridicAiAbonamentPage = React.lazy(() => import('./pages/seo_landings/JuridicAiAbonamentPage'));
const CercetareJuridicaAiPage = React.lazy(() => import('./pages/seo_landings/CercetareJuridicaAiPage'));
const JurisprudentaAiPage = React.lazy(() => import('./pages/seo_landings/JurisprudentaAiPage'));
const AnalizaDocumenteJuridiceAiPage = React.lazy(() => import('./pages/seo_landings/AnalizaDocumenteJuridiceAiPage'));
const SoftwareJuridicAvocatiPage = React.lazy(() => import('./pages/seo_landings/SoftwareJuridicAvocatiPage'));
const TimelinePage = React.lazy(() => import('./pages/TimelinePage'));
const ROIPage = React.lazy(() => import('./pages/ROIPage'));
const ConvertPdfPage = React.lazy(() => import('./pages/ConvertPdfPage'));
const DocumentAnonymizerPage = React.lazy(() => import('./pages/DocumentAnonymizerPage'));
const InstrumenteGratuitePage = React.lazy(() => import('./pages/InstrumenteGratuitePage'));
const ExamenePage = React.lazy(() => import('./pages/ExamenePage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const PersonSearchPage = React.lazy(() => import('./pages/PersonSearchPage'));
const DocxAnalyzerPage = React.lazy(() => import('./pages/DocxAnalyzerPage'));
// Dynamic route pages
const DocumentViewPage = React.lazy(() => import('./pages/DocumentViewPage'));
const AICoursePage = React.lazy(() => import('./pages/AICoursePage'));
const CitizenCoursePage = React.lazy(() => import('./pages/CitizenCoursePage'));
const ExtrasCfPage = React.lazy(() => import('./pages/ExtrasCfPage'));
const CrmPage = React.lazy(() => import('./pages/CrmPage'));

// Company Formation Pages
const CompanyFormationLandingPage = React.lazy(() => import('./pages/CompanyFormation/LandingPage'));
const CompanyFormationWizard = React.lazy(() => import('./pages/CompanyFormation/FormationWizard'));
const CompanyFormationSummary = React.lazy(() => import('./pages/CompanyFormation/SummaryPage'));
const CompanyFormationManual = React.lazy(() => import('./pages/CompanyFormation/ManualPage'));

// Video Juridic
const LegalVideoPage = React.lazy(() => import('./pages/LegalVideo/LegalVideoPage'));
const VideoDetailPage = React.lazy(() => import('./pages/LegalVideo/VideoDetailPage'));
const VideoDashboard = React.lazy(() => import('./pages/EditorDashboard/VideoDashboard'));
const VideoEditor = React.lazy(() => import('./pages/EditorDashboard/VideoEditor'));

// @ts-expect-error: component might not exist in all branches
const AudioQuizContainer = React.lazy(() => import('./features/audio-quiz/AudioQuizContainer'));

// Dictionar Juridic
const DictionarJuridicPage = React.lazy(() => import('./pages/DictionarJuridic/DictionarJuridicPage'));
const DictionarTermenPage = React.lazy(() => import('./pages/DictionarJuridic/DictionarTermenPage'));
const DictionarEditor = React.lazy(() => import('./pages/EditorDashboard/DictionarEditor'));
const ModeleEditor = React.lazy(() => import('./pages/EditorDashboard/ModeleEditor'));

// Agenda Contacte
const AgendaPage = React.lazy(() => import('./pages/Utile/AgendaPage'));
const AgendaContactPage = React.lazy(() => import('./pages/Utile/AgendaContactPage'));
const AgendaEditor = React.lazy(() => import('./pages/EditorDashboard/AgendaEditor'));

// Circumscriptii
const CircumscriptiiPage = React.lazy(() => import('./pages/Utile/CircumscriptiiPage'));
const CircumscriptiiEditor = React.lazy(() => import('./pages/EditorDashboard/CircumscriptiiEditor'));
const IntentLabyrinthPage = React.lazy(() => import('./pages/IntentLabyrinthPage'));

// Dedicated Single-Function Pages
const CautareSpetaPage = React.lazy(() => import('./pages/CautareSpetaPage'));
const CautareCuvinteCheiePage = React.lazy(() => import('./pages/CautareCuvinteCheiePage'));
const VerificareFirmaPage = React.lazy(() => import('./pages/VerificareFirmaPage'));
const ProceseFirmeEcrisPage = React.lazy(() => import('./pages/ProceseFirmeEcrisPage'));
const CautareDosarPage = React.lazy(() => import('./pages/CautareDosarPage'));
const DosareUrmaritePage = React.lazy(() => import('./pages/DosareUrmaritePage'));
const LegislativeChangesPage = React.lazy(() => import('./pages/LegislativeChangesPage'));
const ModeleJuridicePage = React.lazy(() => import('./pages/ModeleJuridicePage'));


// Loading component
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh]">
    <Loader2 className="h-10 w-10 animate-spin text-brand-primary" />
    <p className="mt-4 text-brand-text-secondary">Se încarcă...</p>
  </div>
);

// Smart Root Component
const RootRedirector = () => {
  const { isAuthenticated, isLoading, isReturningUser } = useAuth();
  if (isLoading) return <PageLoader />;

  return isAuthenticated || isReturningUser ? <Navigate to="/cautare" replace /> : <PremiumHomePage />;
};

const AboutRouteWrapper = () => <AboutPage />;
const BlogRouteWrapper = () => <Navigate to="/" replace />;
const BlogArticleRouteWrapper = () => <Navigate to="/" replace />;

const LegacyLoginErrorRedirect = () => {
  const [searchParams] = useSearchParams();
  const requestedCode = searchParams.get('message') || searchParams.get('error') || '';
  const allowedCodes = new Set([
    'callback_failed',
    'email_conflict',
    'google_account_not_found',
    'google_no_code',
    'invalid_oauth_state',
  ]);
  const code = allowedCodes.has(requestedCode) ? requestedCode : 'callback_failed';
  const path = code === 'google_account_not_found' ? '/register' : '/login';
  return <Navigate to={`${path}?oauth_error=${code}`} replace />;
};

const ProtectedRoute: React.FC<{
  settingKey: 'infiintare_firma_enabled' | 'extras_carte_funciara_enabled' | 'asistenta_avocat_enabled';
  serviceName: string;
  element: React.ReactElement;
}> = ({ settingKey, serviceName, element }) => {
  const { settings } = usePublicSettings();
  return settings[settingKey] ? element : <ServiceUnavailable serviceName={serviceName} />;
};



const App: React.FC = () => {
  // Apply SEO rules for non-main domains (noindex duplicate routes)
  useNonMainSEO();

  const location = useLocation();

  React.useEffect(() => {
    const isOfficeEditor =
      location.pathname.startsWith('/editor-document') ||
      location.pathname.startsWith('/en/editor-document') ||
      location.pathname.startsWith('/fr/editor-document') ||
      location.pathname.startsWith('/document-editor');

    if (isOfficeEditor) {
      document.documentElement.classList.add('office-editor-active');
      document
        .querySelectorAll<HTMLLinkElement | HTMLStyleElement>(
          'link[href*="DocumentEditor"], link[href*="casualoffice"], style[data-vite-dev-id*="@casualoffice"], style[data-office-styles], link[data-office-styles]'
        )
        .forEach((el) => {
          el.disabled = false;
        });
    } else {
      document.documentElement.classList.remove('office-editor-active');
      document
        .querySelectorAll<HTMLLinkElement | HTMLStyleElement>(
          'link[href*="DocumentEditor"], link[href*="casualoffice"], style[data-vite-dev-id*="@casualoffice"], style[data-office-styles], link[data-office-styles]'
        )
        .forEach((el) => {
          el.disabled = true;
        });
    }
  }, [location.pathname]);

  return (
    <LanguageProvider>
      <PublicSettingsProvider>
        <AuthProvider>
        <DosarProvider>
          <AnalyticsTracker />
          <ManualProvider>
            <DosarDrawer />
            <ManualDrawer />
            <DosarToast />
            <CookiePolicyFloatingButton />

            <Suspense fallback={<PageLoader />}>
              <ScrollToTopButton />
              <ScrollToTop />
              <SearchSessionBoundary><Routes>
                {DesignSystemPage && <Route path="/__design-system" element={<DesignSystemPage />} />}
                {/* Legislation routes */}

                <Route path="/legislatie/selectie" element={<LegislationSelectionPage />} />
                <Route path="/legislatie/:categorySlug/:itemSlug" element={<LegislatieDetailPage />} />
                <Route path="/legislatie/:categorySlug" element={<LegislatieCategoryPage />} />
                <Route path="/legislatie" element={<LegislatiePage />} />


                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth" element={<Navigate to="/register" replace />} />
                <Route path="/login-error" element={<LegacyLoginErrorRedirect />} />
                <Route path="/complete-profile" element={<CompleteProfilePage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />

                <Route path="/landing" element={<LandingPage />} />
                <Route path="/landing.html" element={<LandingPage />} />
                <Route path="/ghid" element={<IntentLabyrinthPage />} />
                <Route path="/ghid.html" element={<IntentLabyrinthPage />} />
                <Route path="/start" element={<IntentLabyrinthPage />} />
                <Route path="/explorare" element={<IntentLabyrinthPage />} />

                {/* Smart Root Route */}
                <Route path="/" element={<RootRedirector />} />

                {/* Explicit App/Search Route */}
                <Route path="/cautare" element={<SearchPage />} />
                <Route path="/cautare-speta" element={<CautareSpetaPage />} />
                <Route path="/cautare-speta.html" element={<CautareSpetaPage />} />
                <Route path="/cautare-cuvinte-cheie" element={<CautareCuvinteCheiePage />} />
                <Route path="/cautare-cuvinte-cheie.html" element={<CautareCuvinteCheiePage />} />
                <Route path="/verificare-firma" element={<VerificareFirmaPage />} />
                <Route path="/verificare-firma.html" element={<VerificareFirmaPage />} />
                <Route path="/procese-firme-ecris" element={<ProceseFirmeEcrisPage />} />
                <Route path="/procese-firme-ecris.html" element={<ProceseFirmeEcrisPage />} />
                <Route path="/cautare-dosar" element={<CautareDosarPage />} />
                <Route path="/cautare-dosar.html" element={<CautareDosarPage />} />
                <Route path="/dosare-urmarite" element={<DosareUrmaritePage />} />
                <Route path="/dosare-urmarite.html" element={<DosareUrmaritePage />} />
                <Route path="/modificari-legislative" element={<LegislativeChangesPage />} />
                <Route path="/modificari-legislative.html" element={<LegislativeChangesPage />} />
                <Route path="/modele-juridice" element={<ModeleJuridicePage />} />
                <Route path="/modele-juridice.html" element={<ModeleJuridicePage />} />
                <Route path="/cazuri-asemanatoare" element={<PublicCasesPage />} />
                <Route path="/cazuri-asemanatoare.html" element={<PublicCasesPage />} />
                <Route path="/rezultate-cautare" element={<SearchResultsPage />} />
                <Route path="/cautare-persoane" element={<PersonSearchPage />} />
                <Route path="/cautare-persoane.html" element={<PersonSearchPage />} />
                <Route path="/analiza-document" element={<DocxAnalyzerPage />} />
                <Route path="/document/:id" element={<DocumentViewPage />} />

                <Route path="/grid-tests" element={<GridTestsPage />} />
                <Route path="/setari" element={<SettingsPage />} />
                <Route path="/setari/import-analogic" element={<ImportAnalogicPage />} />
                <Route path="/setari/administrare-abonamente" element={<AdminBillingPage />} />

                <Route path="/codul-civil" element={<SearchPage initialSituatie="Codul Civil" pageTitle="Codul Civil Actualizat - Legea Aplicata" />} />
                <Route path="/perspective-juridice" element={<PerspectiveJuridicePage />} />
                <Route path="/codul-de-procedura-civila" element={<SearchPage initialSituatie="Codul de Procedură Civilă" pageTitle="Codul de Procedură Civilă - Legea Aplicata" />} />
                <Route path="/codul-penal" element={<SearchPage initialSituatie="Codul Penal" pageTitle="Codul Penal Actualizat - Legea Aplicata" />} />
                <Route path="/codul-de-procedura-penala" element={<SearchPage initialSituatie="Codul de Procedură Penală" pageTitle="Codul de Procedură Penală - Legea Aplicata" />} />
                <Route path="/codul-muncii" element={<SearchPage initialSituatie="Codul Muncii" pageTitle="Codul Muncii Actualizat - Legea Aplicata" />} />
                <Route path="/codul-administrativ" element={<SearchPage initialSituatie="Codul Administrativ" pageTitle="Codul Administrativ - Legea Aplicata" />} />
                <Route path="/codul-fiscal" element={<SearchPage initialSituatie="Codul Fiscal" pageTitle="Codul Fiscal - Legea Aplicata" />} />
                <Route path="/codul-de-procedura-fiscala" element={<SearchPage initialSituatie="Codul de Procedură Fiscală" pageTitle="Codul de Procedură Fiscală - Legea Aplicata" />} />
                <Route path="/codul-silvic" element={<SearchPage initialSituatie="Codul Silvic" pageTitle="Codul Silvic - Legea Aplicata" />} />

                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/gdpr" element={<GDPRPage />} />
                <Route path="/politica-retur" element={<ReturnPolicyPage />} />
                <Route path="/surse-date" element={<SurseDatePage />} />

                <Route path="/help-center" element={<HelpCenterPage />} />

                <Route path="/test-analysis" element={<TestAnalysisPage />} />
                <Route path="/payment-return" element={<PaymentReturnPage />} />
                <Route path="/abonamente" element={<PricingPage />} />
                <Route path="/subscription" element={<Navigate to="/abonamente" replace />} />
                <Route path="/instagram" element={<InstagramPromoShortcutPage />} />
                <Route path="/promotii/confirmare-email" element={<PromoVerifyEmailPage />} />
                <Route path="/promotii/:slug" element={<PromoLandingPage />} />
                <Route path="/promotii/:slug/regulament" element={<PromoRegulationPage />} />
                <Route path="/promotii/:slug/inscriere" element={<PromoRegisterPage />} />
                <Route path="/taxa-timbru" element={<TaxaTimbruPage />} />
                <Route path="/calcul-termene" element={<CalculTermenePage />} />
                <Route path="/calculator-pensie-alimentara" element={<CalculatorPensieAlimentaraPage />} />
                <Route path="/calculator-termene-legale" element={<CalculatorTermeneLegalePage />} />
                <Route path="/calcul-salariu-net" element={<CalculatorSalariuNetPage />} />
                <Route path="/calcul-procente-2026" element={<CalculProcentePage />} />
                <Route path="/calcul-contopiri" element={<CalculatorContopiriPage />} />
                <Route path="/calcul-limite" element={<CalculatorLimitePage />} />
                <Route path="/calcul-prescriptie-penala" element={<CalculatorPrescriptiePage />} />
                <Route path="/calcul-reabilitare" element={<CalculatorReabilitarePage />} />
                <Route path="/piete-financiare" element={<FinancePage />} />
                <Route path="/piete-financiare.html" element={<Navigate to="/piete-financiare" replace />} />
                <Route path="/piete-financiare/:tab" element={<FinancePage />} />
                <Route path="/piete-financiare/:tab.html" element={<FinancePage />} />
                <Route path="/extras-carte-funciara" element={
                  <ProtectedRoute
                    settingKey="extras_carte_funciara_enabled"
                    serviceName="Extras Carte Funciară"
                    element={<ExtrasCfPage />}
                  />
                } />
                <Route path="/crm" element={<CrmPage />} />

                {/* Company Formation Routes */}
                <Route path="/infiintare-firma" element={
                  <ProtectedRoute
                    settingKey="infiintare_firma_enabled"
                    serviceName="Înființare Firmă"
                    element={<CompanyFormationLandingPage />}
                  />
                } />
                <Route path="/infiintare-firma-wizard" element={
                  <ProtectedRoute
                    settingKey="infiintare_firma_enabled"
                    serviceName="Înființare Firmă"
                    element={<CompanyFormationWizard />}
                  />
                } />
                <Route path="/infiintare-firma/summary" element={
                  <ProtectedRoute
                    settingKey="infiintare_firma_enabled"
                    serviceName="Înființare Firmă"
                    element={<CompanyFormationSummary />}
                  />
                } />
                <Route path="/manual-infiintare-firma" element={
                  <ProtectedRoute
                    settingKey="infiintare_firma_enabled"
                    serviceName="Înființare Firmă"
                    element={<CompanyFormationManual />}
                  />
                } />
                <Route path="/asistenta-avocat" element={
                  <ProtectedRoute
                    settingKey="asistenta_avocat_enabled"
                    serviceName="Asistență Avocat"
                    element={<LawyerAssistancePage />}
                  />
                } />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/despre-noi" element={<AboutRouteWrapper />} />
                <Route path="/securitate" element={<SecuritatePage />} />
                 <Route path="/legaturi-utile" element={<UsefulLinksPage />} />
                 <Route path="/manual-metodologie" element={<ManualMetodologiePage />} />
                  <Route path="/cedo" element={<CedoPage />} />
                  <Route path="/cedo/fise-jurisprudenta" element={<CedoFisePage />} />
                  <Route path="/cedo/fise-jurisprudenta/:slug" element={<CedoFiseDetailPage />} />
                  <Route path="/cedo/:filename" element={<CedoDetailPage />} />
                  <Route path="/practica-neunitara" element={<PracticaNeunitaraPage />} />
                  <Route path="/practica-neunitara/:id" element={<PracticaNeunitaraDetailPage />} />
                  <Route path="/jurnal-practica-judiciara" element={<JurnalPracticaJudiciaraPage />} />
                  <Route path="/jurnal-practica-judiciara/:id" element={<JurnalPracticaJudiciaraDetailPage />} />
                  <Route path="/iccj" element={<IccjPage />} />
                  <Route path="/iccj/:filename" element={<IccjDetailPage />} />
                <Route path="/harta-site" element={<Sitemap />} />
                <Route path="/timeline" element={<TimelinePage />} />
                <Route path="/roi" element={<ROIPage />} />
                <Route path="/instrumente-gratuite" element={<InstrumenteGratuitePage />} />
                <Route path="/convertpdf" element={<ConvertPdfPage />} />
                <Route path="/anonimizare-documente" element={<DocumentAnonymizerPage />} />
                <Route path="/editor-document" element={<DocumentEditorPage />} />
                <Route path="/en/editor-document" element={<DocumentEditorPage />} />
                <Route path="/fr/editor-document" element={<DocumentEditorPage />} />
                <Route path="/ro/editor-document" element={<Navigate to="/editor-document" replace />} />
                <Route path="/editor-document/:sessionId" element={<Navigate to="/editor-document" replace />} />
                <Route path="/en/editor-document/:sessionId" element={<Navigate to="/en/editor-document" replace />} />
                <Route path="/fr/editor-document/:sessionId" element={<Navigate to="/fr/editor-document" replace />} />
                <Route path="/document-editor" element={<Navigate to="/editor-document" replace />} />
                <Route path="/examene" element={<ExamenePage />} />
                <Route path="/audio-legislatie" element={<AudioLegislatiePage />} />
                <Route path="/invatare-activa" element={<ActiveLearningPage />} />
                <Route path="/elemente-de-memorat" element={<MemorizationPage />} />
                <Route path="/elemente-de-memorat.html" element={<Navigate to="/elemente-de-memorat" replace />} />
                <Route path="/scheme-juridice" element={<LegalSchemesPage />} />
                <Route path="/scheme-juridice.html" element={<Navigate to="/scheme-juridice" replace />} />
                <Route path="/audio-grile" element={<AudioQuizContainer />} />

                <Route path="/stiri/drept-civil-si-administrativ" element={<LegalNewsPage initialCategory="Drept Civil și Administrativ" pageTitle="Știri Drept Civil și Administrativ" />} />
                <Route path="/stiri/drept-penal" element={<LegalNewsPage initialCategory="Drept Penal (General)" pageTitle="Știri Drept Penal" />} />
                <Route path="/stiri/dreptul-familiei" element={<LegalNewsPage initialCategory="Dreptul Familiei și Violență Domestică" pageTitle="Știri Dreptul Familiei" />} />
                <Route path="/stiri/criminalitate-organizata" element={<LegalNewsPage initialCategory="Drept Penal: Criminalitate Organizată și Trafic" pageTitle="Știri Criminalitate Organizată" />} />
                <Route path="/stiri/coruptie-si-serviciu" element={<LegalNewsPage initialCategory="Drept Penal: Corupție și Infracțiuni de Serviciu" pageTitle="Știri Corupție și Serviciu" />} />
                <Route path="/stiri/infractiuni-rutiere" element={<LegalNewsPage initialCategory="Drept Penal: Infracțiuni Rutiere" pageTitle="Știri Infracțiuni Rutiere" />} />
                <Route path="/stiri/infractiuni-contra-persoanei" element={<LegalNewsPage initialCategory="Drept Penal: Infracțiuni contra Persoanei" pageTitle="Știri Infracțiuni contra Persoanei" />} />
                <Route path="/stiri/infractiuni-contra-patrimoniului" element={<LegalNewsPage initialCategory="Drept Penal: Infracțiuni contra Patrimoniului" pageTitle="Știri Infracțiuni contra Patrimoniului" />} />
                <Route path="/stiri/infractiuni-economice" element={<LegalNewsPage initialCategory="Drept Penal: Infracțiuni Economice și Fals" pageTitle="Știri Infracțiuni Economice" />} />

                <Route path="/stiri/jurisprudenta" element={<LegalNewsPage initialCategory="Jurisprudență (General)" pageTitle="Noutăți Jurisprudență" />} />

                <Route path="/raport-predictiv" element={<PredictiveReportPage />} />

                {/* Blog Routes (Dynamic based on domain) */}
                <Route path="/blog" element={<BlogRouteWrapper />} />
                <Route path="/blog/:slug" element={<BlogArticleRouteWrapper />} />

                {/* Editor Routes */}
                <Route path="/editor/dashboard" element={<EditorDashboard />} />
                <Route path="/editor/new" element={<ArticleEditor />} />
                <Route path="/editor/edit/:id" element={<ArticleEditor />} />
                <Route path="/editor/ecris-ingest" element={<EcrisIngestPage />} />

                {/* Dictionar Juridic Editor Routes */}
                <Route path="/editor/dictionar" element={<DictionarEditor />} />
                <Route path="/editor/dictionar/new" element={<DictionarEditor />} />
                <Route path="/editor/dictionar/edit/:id" element={<DictionarEditor />} />

                {/* Agenda Editor Routes */}
                <Route path="/editor/agenda" element={<AgendaEditor />} />

                {/* Modele si Formulare Editor Routes */}
                <Route path="/editor/modele" element={<ModeleEditor />} />

                {/* Video Juridic Editor Routes */}
                <Route path="/editor/video-dashboard" element={<VideoDashboard />} />
                <Route path="/editor/video-new" element={<VideoEditor />} />
                <Route path="/editor/video-edit/:id" element={<VideoEditor />} />

                <Route path="/stiri/autor/:authorId" element={<ProfessionalProfilePage />} />
                <Route path="/stiri" element={<LegalNewsPage />} />
                <Route path="/stiri.html" element={<LegalNewsPage />} />
                <Route path="/articole-juridice" element={<LegalNewsPage />} />
                <Route path="/articole-juridice.html" element={<LegalNewsPage />} />
                <Route path="/publicare-articole-juridice" element={<PublicareArticolePage />} />
                <Route path="/publicare-articole-juridice.html" element={<PublicareArticolePage />} />
                <Route path="/site-uri-juridice" element={<UsefulLinksPage />} />
                <Route path="/site-uri-juridice.html" element={<UsefulLinksPage />} />
                <Route path="/stiri/articol/:slug" element={<ArticleDetailPage />} />

                {/* Video Juridic */}
                <Route path="/video-juridic" element={<LegalVideoPage />} />
                <Route path="/video-juridic/:slug" element={<VideoDetailPage />} />

                {/* Dictionar Juridic */}
                <Route path="/dictionar-juridic" element={<DictionarJuridicPage />} />
                <Route path="/dictionar-juridic/:slug" element={<DictionarTermenPage />} />

                {/* Agenda Contacte */}
                <Route path="/utile/agenda-contacte" element={<AgendaPage />} />
                <Route path="/utile/agenda-contacte/:slug" element={<AgendaContactPage />} />

                {/* Circumscriptii */}
                <Route path="/utile/circumscriptii" element={<CircumscriptiiPage />} />
                <Route path="/editor/circumscriptii" element={<CircumscriptiiEditor />} />

                <Route path="/evenimente" element={<LegalNewsPage />} />
                <Route path="/evenimente/curs-ai-avocati-juristi" element={<AICoursePage />} />
                <Route path="/evenimente/ghid-cetateni-dosar-avocat" element={<CitizenCoursePage />} />
                <Route path="/editura" element={<LegalNewsPage />} />
                <Route path="/cariere" element={<LegalNewsPage />} />
                <Route path="/profesionisti" element={<LegalNewsPage />} />

                {/* Landing Pages */}
                <Route path="/prezentare" element={<RootRedirector />} />

                {/* SEO Landing Pages */}
                {/* SEO Landing Pages */}
                <Route path="/agenda-juridica-online.html" element={<AgendaJuridicaPage />} />
                <Route path="/agenda-juridica" element={<AgendaJuridicaPage />} />
                <Route path="/juristul-tau-virtual.html" element={<JuristulVirtualPage />} />
                <Route path="/asistent-juridic-romania.html" element={<AsistentJuridicPage />} />
                <Route path="/inteligenta-artificiala-juridica.html" element={<InteligentArtificialaPage />} />
                <Route path="/acces-justitie-online.html" element={<AccesJustitiePage />} />
                <Route path="/colegul-tau-ai-juridic.html" element={<ColegulAIPage />} />
                <Route path="/studenti-drept-partener-studiu.html" element={<StudentiPage />} />
                <Route path="/predictie-juridica-analytics.html" element={<PredictieJuridicaPage />} />
                <Route path="/resurse-juridice-actualizate.html" element={<ResurseJuridicePage />} />
                <Route path="/crm-stocare-privata.html" element={<CrmStocarePrivataPage />} />
                <Route path="/crm-stocare-privata" element={<CrmStocarePrivataPage />} />
                <Route path="/ai-pentru-juristi.html" element={<AiPentruJuristiPage />} />
                <Route path="/ai-pentru-juristi" element={<AiPentruJuristiPage />} />
                <Route path="/ai-juridic-romania.html" element={<AiJuridicRomaniaPage />} />
                <Route path="/ai-juridic-romania" element={<AiJuridicRomaniaPage />} />
                <Route path="/cel-mai-bun-ai-juridic.html" element={<CelMaiBunAiJuridicPage />} />
                <Route path="/cel-mai-bun-ai-juridic" element={<CelMaiBunAiJuridicPage />} />
                <Route path="/asistent-juridic-ai.html" element={<AsistentJuridicAiPage />} />
                <Route path="/asistent-juridic-ai" element={<AsistentJuridicAiPage />} />
                <Route path="/juridic-ai-abonament.html" element={<JuridicAiAbonamentPage />} />
                <Route path="/juridic-ai-abonament" element={<JuridicAiAbonamentPage />} />

                <Route path="/cercetare-juridica-ai.html" element={<CercetareJuridicaAiPage />} />
                <Route path="/jurisprudenta-ai.html" element={<JurisprudentaAiPage />} />
                <Route path="/analiza-documente-juridice-ai.html" element={<AnalizaDocumenteJuridiceAiPage />} />
                <Route path="/software-juridic-avocati.html" element={<SoftwareJuridicAvocatiPage />} />

                {/* Legacy Subscriptions 301 Redirects */}
                <Route path="/abonament-juridic.html" element={<Navigate to="/juridic-ai-abonament.html" replace />} />
                <Route path="/juridic-abonament.html" element={<Navigate to="/juridic-ai-abonament.html" replace />} />
                <Route path="/abonament-juridic" element={<Navigate to="/juridic-ai-abonament.html" replace />} />
                <Route path="/juridic-abonament" element={<Navigate to="/juridic-ai-abonament.html" replace />} />


                <Route path="*" element={<NotFoundPage />} />
              </Routes></SearchSessionBoundary>
              <ManualButton />
            </Suspense>
          </ManualProvider>
        </DosarProvider>
      </AuthProvider>
      </PublicSettingsProvider>
    </LanguageProvider>
  );

};

export default App;
